import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import JobSearchPage from './pages/JobSearchPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Contact from './pages/Contact';
import BackToTop from './components/BackToTop';
import { logNavigation, logUserInteraction } from './lib/logger';
import './App.css';

// Component to track route changes
function RouteTracker() {
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState<string>('');

  useEffect(() => {
    if (previousPath && previousPath !== location.pathname) {
      logNavigation(previousPath, location.pathname, 'browser');
    }
    setPreviousPath(location.pathname);
  }, [location.pathname, previousPath]);

  return null;
}

// Logo component with navigation
function Logo() {
  const navigate = useNavigate();
  
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('Logo clicked - navigating to home');
    logUserInteraction('reset_search', 'logo_click');
    navigate('/');
  };

  return (
    <Link 
      to="/" 
      className="flex items-center text-xl font-light text-[#1F2937] hover:text-[#4F46E5] transition-colors duration-200 cursor-pointer tracking-tight"
      onClick={handleLogoClick}
      aria-label="WageWise - H1B Visa Job Search Platform Homepage"
    >
      <img 
        src={`${process.env.PUBLIC_URL}/wagewise_logo.png`}
        alt="WageWise Logo - H1B Visa Job Search Platform" 
        className="w-8 h-8 mr-1"
        width="32"
        height="32"
      />
      <span className="font-semibold">Wage</span>Wise
    </Link>
  );
}

function App() {
  return (
    <Router>
      <RouteTracker />
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
        {/* Main Navigation */}
        <nav className="bg-white shadow-sm border-b border-[#E5E7EB]" role="navigation" aria-label="Main navigation">
          <div className="max-w-6xl mx-auto py-2 px-4 flex justify-between items-center">
            <Logo />
            
            {/* Skip to main content link for accessibility */}
            <a 
              href="#main-content" 
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-[#4F46E5] text-white px-4 py-2 rounded-md z-50"
            >
              Skip to main content
            </a>
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="flex-grow" id="main-content" role="main">
          <Routes>
            <Route path="/" element={<JobSearchPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>
        <footer className="bg-[#F3F4F6] py-6 mt-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-4 md:mb-0">
                <p className="text-[#4B5563] text-sm">&copy; {new Date().getFullYear()} WageWise. All rights reserved.</p>
              </div>
              <div className="flex space-x-8">
                <Link 
                  to="/privacy" 
                  className="text-[#4B5563] hover:text-[#1F2937] text-sm transition-colors duration-150"
                  onClick={() => logUserInteraction('click', 'footer_privacy_link')}
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/terms" 
                  className="text-[#4B5563] hover:text-[#1F2937] text-sm transition-colors duration-150"
                  onClick={() => logUserInteraction('click', 'footer_terms_link')}
                >
                  Terms of Service
                </Link>
                <Link 
                  to="/contact" 
                  className="text-[#4B5563] hover:text-[#1F2937] text-sm transition-colors duration-150"
                  onClick={() => logUserInteraction('click', 'footer_contact_link')}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </footer>
        
        {/* Back to Top Button */}
        <BackToTop />
      </div>
    </Router>
  );
}

export default App;
